import React, { useEffect, useState, useContext, useCallback, useMemo } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import "../styles/Stocuri.css";
import { saveAs } from "file-saver";

const API_BASE = "https://tj.marenergy.ro/api.php";

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

function Modal({ open, title, children, onClose, footer, width = "min(920px, 100%)" }) {
  if (!open) return null;
  return (
    <div className="stocuri-modal-backdrop" role="dialog" aria-modal="true">
      <div className="stocuri-modal" style={{ width }}>
        <div className="stocuri-modal-header">
          <div className="stocuri-modal-title">{title}</div>
          <button className="stocuri-icon-btn" onClick={onClose} aria-label="Închide">
            ✕
          </button>
        </div>
        <div className="stocuri-modal-body">{children}</div>
        {footer && <div className="stocuri-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

function Pill({ children, tone = "default" }) {
  return <span className={cx("stocuri-pill", `stocuri-pill-${tone}`)}>{children}</span>;
}

function formatDateTime(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

function roleNumber(user) {
  const r = user?.role;
  const n = typeof r === "string" ? parseInt(r, 10) : Number(r);
  return Number.isFinite(n) ? n : 0;
}

const Stock = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const role = useMemo(() => roleNumber(user), [user]);
  const canSeeStock = role === 1 || role === 2;
  const canSeeAllOrders = role === 1 || role === 2;

  // Tabs
  const [activeTab, setActiveTab] = useState("stock"); // stock | orders

  // Stock
  const [stock, setStock] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activeLogsStockId, setActiveLogsStockId] = useState(null);
  const [stockError, setStockError] = useState(null);
  const [stockLoading, setStockLoading] = useState(true);
  const [showCustodie, setShowCustodie] = useState(false);

  // Stock UI filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [lowStockOnly, setLowStockOnly] = useState(false);

  // Add product modal (role1)
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    category: "panou",
    quantity: 0,
    min_quantity: 1,
    price: 0,
  });
  const [addCodes, setAddCodes] = useState([]);

  // Edit product modal (role1)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    product_id: null,
    name: "",
    min_quantity: 1,
    price: 0,
    category: "",
  });

  // Custodie move back modal (role1)
  const [showMoveBackModal, setShowMoveBackModal] = useState(false);
  const [moveBackCategory, setMoveBackCategory] = useState("");
  const [moveBackProducts, setMoveBackProducts] = useState([]);
  const [moveBackSearch, setMoveBackSearch] = useState("");
  const [moveBackSelectedCodes, setMoveBackSelectedCodes] = useState([]);

  // Old custodie quick issue modal (optional, role1) - still useful
  const [showCustodieModal, setShowCustodieModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCodes, setSelectedCodes] = useState([]);

  // Orders
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersSearch, setOrdersSearch] = useState("");

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null); // {order, items, assignees, notes, events}

  // Create order modal (role1)
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [createOrderForm, setCreateOrderForm] = useState({
    title: "",
    delegate_name: "",
    assignee_ids: [],
    items: [], // {product_id, name, category, requested_qty}
  });

  // Users list for assignment (best effort)
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersList, setUsersList] = useState([]);

  // For create order: add items from products list
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerSearch, setProductPickerSearch] = useState("");
  const [productsForPicker, setProductsForPicker] = useState([]);
  const [productsPickerLoading, setProductsPickerLoading] = useState(false);
  const [productsPickerError, setProductsPickerError] = useState(null);

  // Notes modal (optional)
  const [showNoteModal, setShowNoteModal] = useState(false);

  // Issue/mount inputs inside order details
  const [issueDraft, setIssueDraft] = useState({}); // order_item_id -> qty
  const [mountDraft, setMountDraft] = useState({}); // order_item_id -> qty
  const [noteDraft, setNoteDraft] = useState("");

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  // Default tab based on role
  useEffect(() => {
    if (role === 3) setActiveTab("orders");
    if (!canSeeStock && activeTab === "stock") setActiveTab("orders");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, canSeeStock]);

  // --------------------------
  // Stock: fetchers
  // --------------------------
  const fetchStock = useCallback(() => {
    if (!canSeeStock) return;
    setStockLoading(true);
    setStockError(null);

    const action = showCustodie ? "get_stock_custodie" : "get_stock";

    axios
      .get(`${API_BASE}?action=${action}`, { headers: authHeaders })
      .then((res) => setStock(res.data || []))
      .catch(() => setStockError("Eroare la încărcarea stocurilor"))
      .finally(() => setStockLoading(false));
  }, [showCustodie, authHeaders, canSeeStock]);

  const fetchProductsByCategory = (category) => {
    if (!category) {
      setFilteredProducts([]);
      return;
    }

    axios
      .get(`${API_BASE}?action=get_stock_by_category&category=${encodeURIComponent(category)}`, {
        headers: authHeaders,
      })
      .then((res) => setFilteredProducts(res.data || []))
      .catch(() => alert("Eroare la încărcarea produselor pe categorie"));
  };

  const fetchCustodieProductsByCategory = (category) => {
    // move back uses get_stock_custodie + local filter
    if (!category) {
      setMoveBackProducts([]);
      return;
    }

    axios
      .get(`${API_BASE}?action=get_stock_custodie`, { headers: authHeaders })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setMoveBackProducts(rows.filter((r) => String(r.category) === String(category)));
      })
      .catch(() => alert("Eroare la încărcarea produselor din custodie"));
  };

  const fetchLogs = (id) => {
    if (!canSeeStock) return;
    if (activeLogsStockId === id) {
      setActiveLogsStockId(null);
      setLogs([]);
      return;
    }

    axios
      .get(`${API_BASE}?action=get_stock_logs&stock_id=${id}`, { headers: authHeaders })
      .then((res) => {
        setActiveLogsStockId(id);
        setLogs(res.data || []);
      })
      .catch(() => alert("Eroare la încărcarea logurilor"));
  };

  // --------------------------
  // Orders: fetchers
  // --------------------------
  const fetchOrders = useCallback(() => {
    setOrdersLoading(true);
    setOrdersError(null);
    const q = (ordersSearch || "").trim();
    const url = `${API_BASE}?action=get_stock_orders${q ? `&q=${encodeURIComponent(q)}` : ""}`;

    axios
      .get(url, { headers: authHeaders })
      .then((res) => setOrders(res.data || []))
      .catch(() => setOrdersError("Eroare la încărcarea comenzilor"))
      .finally(() => setOrdersLoading(false));
  }, [ordersSearch, authHeaders]);

  const fetchOrderDetails = useCallback(
    (orderId) => {
      if (!orderId) return;
      setOrderDetailsLoading(true);
      setOrderDetailsError(null);
      setOrderDetails(null);

      axios
        .get(`${API_BASE}?action=get_stock_order_details&order_id=${orderId}`, { headers: authHeaders })
        .then((res) => {
          setOrderDetails(res.data || null);
          setSelectedOrderId(orderId);
          setIssueDraft({});
          setMountDraft({});
          setNoteDraft("");
          setShowNoteModal(false);
        })
        .catch(() => setOrderDetailsError("Eroare la încărcarea detaliilor comenzii"))
        .finally(() => setOrderDetailsLoading(false));
    },
    [authHeaders]
  );

  // Best-effort: users list for assignment (optional endpoint)
  const fetchUsers = useCallback(() => {
    if (role !== 1) return;
    setUsersLoading(true);
    axios
      .get(`${API_BASE}?action=get_users`, { headers: authHeaders })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setUsersList(list);
      })
      .catch(() => setUsersList([]))
      .finally(() => setUsersLoading(false));
  }, [role, authHeaders]);

  // Best-effort: products list for picker (reuse get_stock depozit and dedupe by product_id)
  const fetchProductsForPicker = useCallback(() => {
    setProductsPickerLoading(true);
    setProductsPickerError(null);

    axios
      .get(`${API_BASE}?action=get_stock`, { headers: authHeaders })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const map = new Map();
        rows.forEach((r) => {
          const pid = r.product_id ?? `${r.name}-${r.category}`;
          if (!map.has(pid)) map.set(pid, r);
        });
        setProductsForPicker(Array.from(map.values()));
      })
      .catch(() => setProductsPickerError("Nu pot încărca lista de produse pentru comenzi."))
      .finally(() => setProductsPickerLoading(false));
  }, [authHeaders]);

  // --------------------------
  // Init
  // --------------------------
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (canSeeStock) fetchStock();
    fetchOrders();
    if (role === 1) fetchUsers();
  }, [user, navigate, fetchStock, fetchOrders, canSeeStock, role, fetchUsers]);

  // Refetch stock when toggling depozit/custodie
  useEffect(() => {
    if (canSeeStock) fetchStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustodie]);

  // --------------------------
  // Stock: derived
  // --------------------------
  const visibleStock = useMemo(() => {
    const s = (search || "").trim().toLowerCase();

    return (stock || [])
      .filter((it) => {
        if (categoryFilter !== "all" && String(it.category) !== String(categoryFilter)) return false;
        if (lowStockOnly) {
          const q = parseInt(it.quantity, 10) || 0;
          const minq = parseInt(it.min_quantity, 10) || 0;
          if (!(q < minq)) return false;
        }
        if (!s) return true;

        const hay = `${it.name || ""} ${it.category || ""} ${it.code || ""}`.toLowerCase();
        return hay.includes(s);
      })
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ro"));
  }, [stock, search, categoryFilter, lowStockOnly]);

  const categoryLabel = (c) => {
    switch (c) {
      case "panou":
        return "Panou";
      case "acumulator":
        return "Acumulator";
      case "invertor":
        return "Invertor";
      case "smart_meter":
        return "Smart Meter";
      case "altele":
        return "Altele";
      default:
        return c || "-";
    }
  };

  // --------------------------
  // Stock: actions
  // --------------------------
  const resetAddForm = () => {
    setAddForm({ name: "", category: "panou", quantity: 0, min_quantity: 1, price: 0 });
    setAddCodes([]);
  };

  const openAddModal = () => {
    resetAddForm();
    setShowAddModal(true);
  };

  const onAddInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      const qty = Math.max(0, parseInt(value, 10) || 0);
      setAddForm((p) => ({ ...p, quantity: qty }));

      setAddCodes((prev) => {
        const next = Array(qty).fill("");
        for (let i = 0; i < Math.min(prev.length, next.length); i++) next[i] = prev[i] || "";
        return next;
      });
      return;
    }

    setAddForm((p) => ({ ...p, [name]: value }));
  };

  const onAddCodeChange = (index, value) => {
    setAddCodes((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const saveAddStock = () => {
    const trimmedName = (addForm.name || "").trim();
    if (!trimmedName) return alert("Completează numele produsului.");
    if (!addForm.quantity || addForm.quantity < 1) return alert("Cantitatea trebuie să fie cel puțin 1.");

    const cleanedCodes = addCodes.map((c) => String(c || "").trim()).filter(Boolean);

    if (cleanedCodes.length !== addForm.quantity) {
      return alert("Completează toate codurile (exact câte produse ai setat la cantitate).");
    }
    const uniqueCodes = new Set(cleanedCodes);
    if (uniqueCodes.size !== cleanedCodes.length) return alert("Codurile trebuie să fie unice!");

    axios
      .post(
        API_BASE,
        {
          action: "add_stock",
          name: trimmedName,
          category: addForm.category,
          price: Number(addForm.price) || 0,
          min_quantity: Number(addForm.min_quantity) || 1,
          codes: cleanedCodes,
        },
        { headers: authHeaders }
      )
      .then(() => {
        fetchStock();
        setShowAddModal(false);
        resetAddForm();
      })
      .catch(() => alert("Eroare la salvarea stocului"));
  };

  const adjustQuantity = (id, type) => {
    const label = type === "in" ? "adăugat" : "scăzut";
    const qty = prompt(`Cantitate de ${label}:`);
    if (!qty || isNaN(qty)) return;

    axios
      .post(
        API_BASE,
        { action: "adjust_stock_quantity", stock_id: id, change_type: type, quantity: parseInt(qty, 10) },
        { headers: authHeaders }
      )
      .then(() => fetchStock())
      .catch(() => alert("Eroare la ajustarea cantității"));
  };

  const openEditProduct = (row) => {
    if (role !== 1) return;
    setEditForm({
      product_id: row.product_id,
      name: row.name || "",
      min_quantity: Number(row.min_quantity) || 1,
      price: Number(row.price) || 0,
      category: row.category || "",
    });
    setShowEditModal(true);
  };

  const saveEditProduct = () => {
    if (!editForm.product_id) return;
    const nm = (editForm.name || "").trim();
    if (!nm) return alert("Completează numele produsului.");

    axios
      .post(
        API_BASE,
        {
          action: "edit_stock_product",
          product_id: editForm.product_id,
          name: nm,
          min_quantity: Number(editForm.min_quantity) || 0,
          price: Number(editForm.price) || 0,
        },
        { headers: authHeaders }
      )
      .then(() => {
        setShowEditModal(false);
        fetchStock();
      })
      .catch(() => alert("Eroare la editarea produsului"));
  };

  const toggleCodeSelected = (code) => {
    setSelectedCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const moveToCustodieQuick = (codesToMove) => {
    if (!codesToMove || codesToMove.length === 0) return alert("Selectează cel puțin un produs!");
    if (role !== 1) return alert("Nu ai permisiuni.");

    axios
      .post(API_BASE, { action: "move_to_custodie", codes: codesToMove }, { headers: authHeaders })
      .then(() => {
        fetchStock();
        setShowCustodieModal(false);
        setSelectedCodes([]);
        setSelectedCategory("");
        setFilteredProducts([]);
        setSearchTerm("");
      })
      .catch(() => alert("Eroare la mutarea în custodie"));
  };

  const toggleMoveBackCode = (code) => {
    setMoveBackSelectedCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const moveToDepozit = () => {
    if (role !== 1) return alert("Nu ai permisiuni.");
    if (!moveBackSelectedCodes.length) return alert("Selectează cel puțin un cod din custodie.");

    axios
      .post(API_BASE, { action: "move_to_depozit", codes: moveBackSelectedCodes }, { headers: authHeaders })
      .then(() => {
        setShowMoveBackModal(false);
        setMoveBackSelectedCodes([]);
        setMoveBackCategory("");
        setMoveBackProducts([]);
        setMoveBackSearch("");
        fetchStock();
      })
      .catch(() => alert("Eroare la mutarea înapoi în depozit"));
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(visibleStock);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stocuri");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, showCustodie ? "stocuri-custodie.xlsx" : "stocuri.xlsx");
  };

  // --------------------------
  // Orders: actions
  // --------------------------
  const openCreateOrderModal = () => {
    if (role !== 1) return;
    setCreateOrderForm({ title: "", delegate_name: "", assignee_ids: [], items: [] });
    setProductPickerOpen(false);
    setProductsForPicker([]);
    setProductPickerSearch("");
    setShowCreateOrder(true);
  };

  const addItemToOrder = (product) => {
    if (!product) return;
    const pid = product.product_id ?? product.id;
    if (!pid) return;

    setCreateOrderForm((prev) => {
      const exists = prev.items.some((it) => String(it.product_id) === String(pid));
      if (exists) return prev;
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            product_id: pid,
            name: product.name || "",
            category: product.category || "",
            requested_qty: 1,
          },
        ],
      };
    });
  };

  const removeOrderItem = (product_id) => {
    setCreateOrderForm((prev) => ({
      ...prev,
      items: prev.items.filter((it) => String(it.product_id) !== String(product_id)),
    }));
  };

  const setOrderItemQty = (product_id, qty) => {
    const n = Math.max(0, parseInt(qty, 10) || 0);
    setCreateOrderForm((prev) => ({
      ...prev,
      items: prev.items.map((it) => (String(it.product_id) === String(product_id) ? { ...it, requested_qty: n } : it)),
    }));
  };

  const toggleAssignee = (uid) => {
    const id = Number(uid);
    if (!id) return;
    setCreateOrderForm((prev) => {
      const has = prev.assignee_ids.includes(id);
      return { ...prev, assignee_ids: has ? prev.assignee_ids.filter((x) => x !== id) : [...prev.assignee_ids, id] };
    });
  };

  const createOrder = () => {
    if (role !== 1) return alert("Nu ai permisiuni.");
    const title = (createOrderForm.title || "").trim();
    if (!title) return alert("Completează numele comenzii (titlu).");

    const items = (createOrderForm.items || [])
      .map((it) => ({
        product_id: Number(it.product_id),
        requested_qty: Number(it.requested_qty) || 0,
      }))
      .filter((it) => it.product_id > 0 && it.requested_qty > 0);

    if (!items.length) return alert("Adaugă cel puțin un produs cu cantitate > 0.");

    axios
      .post(
        API_BASE,
        {
          action: "create_stock_order",
          title,
          delegate_name: (createOrderForm.delegate_name || "").trim() || null,
          assignee_ids: (createOrderForm.assignee_ids || []).map(Number).filter(Boolean),
          items,
        },
        { headers: authHeaders }
      )
      .then((res) => {
        setShowCreateOrder(false);
        fetchOrders();
        const oid = res?.data?.order_id;
        if (oid) fetchOrderDetails(oid);
      })
      .catch(() => alert("Eroare la crearea comenzii"));
  };

  const issueToCustodie = () => {
    if (role !== 1) return alert("Doar rol 1 poate da spre custodie.");

    const items = orderDetails?.items || [];
    const issues = items
      .map((it) => {
        const qty = parseInt(issueDraft[it.id], 10) || 0;
        return { order_item_id: it.id, qty };
      })
      .filter((x) => x.qty > 0);

    if (!issues.length) return alert("Completează cantitățile de dat în custodie (minim 1).");

    axios
      .post(API_BASE, { action: "issue_order_to_custodie", order_id: selectedOrderId, issues }, { headers: authHeaders })
      .then(() => fetchOrderDetails(selectedOrderId))
      .catch(() => alert("Eroare la trimiterea în custodie (issue)"));
  };

  const mountItems = () => {
    const canMount = role === 1 || role === 2 || role === 3;
    if (!canMount) return alert("Nu ai permisiuni.");

    const items = orderDetails?.items || [];
    const mounts = items
      .map((it) => {
        const qty = parseInt(mountDraft[it.id], 10) || 0;
        return { order_item_id: it.id, qty };
      })
      .filter((x) => x.qty > 0);

    if (!mounts.length) return alert("Completează cantitățile de montat (minim 1).");

    axios
      .post(
        API_BASE,
        { action: "mount_order_items", order_id: selectedOrderId, mounts, note: noteDraft?.trim() || null },
        { headers: authHeaders }
      )
      .then(() => fetchOrderDetails(selectedOrderId))
      .catch(() => alert("Eroare la montare"));
  };

  const issueSingleToCustodie = (orderItemId) => {
    if (role !== 1) return alert("Doar rol 1 poate da spre custodie.");
    if (!selectedOrderId) return;
  
    const qty = parseInt(issueDraft[orderItemId], 10) || 0;
    if (qty <= 0) return alert("Completează cantitatea pentru acest produs.");
  
    axios
      .post(
        API_BASE,
        { action: "issue_order_to_custodie", order_id: selectedOrderId, issues: [{ order_item_id: orderItemId, qty }] },
        { headers: authHeaders }
      )
      .then(() => fetchOrderDetails(selectedOrderId))
      .catch(() => alert("Eroare la trimiterea în custodie (issue)"));
  };
  
  const mountSingleItem = (orderItemId) => {
    const canMount = role === 1 || role === 2 || role === 3;
    if (!canMount) return alert("Nu ai permisiuni.");
    if (!selectedOrderId) return;
  
    const qty = parseInt(mountDraft[orderItemId], 10) || 0;
    if (qty <= 0) return alert("Completează cantitatea pentru acest produs.");
  
    axios
      .post(
        API_BASE,
        {
          action: "mount_order_items",
          order_id: selectedOrderId,
          mounts: [{ order_item_id: orderItemId, qty }],
          note: noteDraft?.trim() || null,
        },
        { headers: authHeaders }
      )
      .then(() => fetchOrderDetails(selectedOrderId))
      .catch(() => alert("Eroare la montare"));
  };  

  const addOrderNote = () => {
    const note = (noteDraft || "").trim();
    if (!note) return alert("Scrie o notă.");

    axios
      .post(API_BASE, { action: "add_stock_order_note", order_id: selectedOrderId, note }, { headers: authHeaders })
      .then(() => {
        setNoteDraft("");
        fetchOrderDetails(selectedOrderId);
      })
      .catch(() => alert("Eroare la adăugarea notei"));
  };

  const closeOrder = () => {
    const ok = window.confirm("Sigur vrei să închizi comanda?");
    if (!ok) return;

    axios
      .post(API_BASE, { action: "close_stock_order", order_id: selectedOrderId }, { headers: authHeaders })
      .then(() => fetchOrderDetails(selectedOrderId))
      .catch(() => alert("Eroare la închiderea comenzii"));
  };

  // --------------------------
  // UI helpers
  // --------------------------
  const orderStatusLabel = (s) => {
    switch (s) {
      case "open":
        return <Pill tone="info">Deschisă</Pill>;
      case "in_custodie":
        return <Pill tone="warning">În custodie</Pill>;
      case "closed":
        return <Pill tone="success">Închisă</Pill>;
      default:
        return <Pill tone="default">{s || "—"}</Pill>;
    }
  };

  const filteredOrders = useMemo(() => {
    const s = (ordersSearch || "").trim().toLowerCase();
    const list = Array.isArray(orders) ? orders : [];
    if (!s) return list;
    return list.filter((o) => String(o.title || "").toLowerCase().includes(s));
  }, [orders, ordersSearch]);

  // --------------------------
  // Render
  // --------------------------
  return (
    <div className="stocuri-container">
      <div className="stocuri-header">
        <div className="stocuri-title">
          <div className="stocuri-title-badge">📦</div>
          <div>
            <div className="stocuri-title-text">Stocuri & Comenzi</div>
            <div className="stocuri-subtitle">
              Rol: {role || "—"} • {activeTab === "stock" ? (showCustodie ? "Custodie" : "Depozit") : "Comenzi"}
            </div>
          </div>
        </div>

        <div className="stocuri-tabs">
          {canSeeStock && (
            <button
              className={cx("stocuri-tab", activeTab === "stock" && "stocuri-tab-active")}
              onClick={() => setActiveTab("stock")}
            >
              📦 Stoc
            </button>
          )}
          <button
            className={cx("stocuri-tab", activeTab === "orders" && "stocuri-tab-active")}
            onClick={() => setActiveTab("orders")}
          >
            🧾 Comenzi
          </button>
        </div>

        {activeTab === "stock" && canSeeStock && (
          <div className="stocuri-toolbar">
            <div className="stocuri-search">
              <span className="stocuri-search-icon">⌕</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caută după nume / cod / categorie…"
              />
            </div>

            <select className="stocuri-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">Toate categoriile</option>
              <option value="panou">Panou</option>
              <option value="acumulator">Acumulator</option>
              <option value="invertor">Invertor</option>
              <option value="smart_meter">Smart Meter</option>
              <option value="altele">Altele</option>
            </select>

            <label className="stocuri-check">
              <input type="checkbox" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />
              <span>Doar sub minim</span>
            </label>

            <div className="stocuri-actions">
              <button className="stocuri-btn stocuri-btn-ghost" onClick={fetchStock} title="Refresh">
                ⟲
              </button>

              <button className="stocuri-btn stocuri-btn-ghost" onClick={exportExcel}>
                ⬇️ Export
              </button>

              <button
                className={cx("stocuri-btn", showCustodie ? "stocuri-btn-secondary" : "stocuri-btn-ghost")}
                onClick={() => {
                  setShowCustodie((p) => !p);
                  setActiveLogsStockId(null);
                  setLogs([]);
                }}
              >
                {showCustodie ? "📦 Depozit" : "🏢 Custodie"}
              </button>

              {role === 1 && !showCustodie && (
                <>
                  <button className="stocuri-btn stocuri-btn-primary" onClick={openAddModal}>
                    ➕ Adaugă
                  </button>
                  <button className="stocuri-btn stocuri-btn-secondary" onClick={() => setShowCustodieModal(true)}>
                    🏢 Spre custodie (rapid)
                  </button>
                </>
              )}

              {role === 1 && showCustodie && (
                <button
                  className="stocuri-btn stocuri-btn-secondary"
                  onClick={() => {
                    setShowMoveBackModal(true);
                    setMoveBackSelectedCodes([]);
                    setMoveBackCategory("");
                    setMoveBackProducts([]);
                    setMoveBackSearch("");
                  }}
                >
                  ↩️ Înapoi în depozit
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === "orders" && (
          <div className="stocuri-toolbar stocuri-toolbar-orders">
            <div className="stocuri-search">
              <span className="stocuri-search-icon">⌕</span>
              <input
                value={ordersSearch}
                onChange={(e) => setOrdersSearch(e.target.value)}
                placeholder="Caută după numele comenzii…"
              />
            </div>

            <div className="stocuri-actions">
              <button className="stocuri-btn stocuri-btn-ghost" onClick={fetchOrders}>
                ⟲ Refresh
              </button>

              {role === 1 && (
                <button className="stocuri-btn stocuri-btn-primary" onClick={openCreateOrderModal}>
                  ➕ Comandă nouă
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* STOCK TAB */}
      {activeTab === "stock" && canSeeStock && (
        <>
          {stockLoading && <div className="stocuri-state">Se încarcă…</div>}
          {stockError && <div className="stocuri-state stocuri-state-error">{stockError}</div>}

          {!stockLoading && (
            <div className="stocuri-card">
              <div className="stocuri-table-responsive">
                <table className="stocuri-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Produs</th>
                      <th>Categorie</th>
                      <th>Cod</th>
                      <th>Cant.</th>
                      <th>Minim</th>
                      <th>Preț</th>
                      <th style={{ width: 320 }}>Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStock.map((item) => {
                      const q = parseInt(item.quantity, 10) || 0;
                      const minq = parseInt(item.min_quantity, 10) || 0;
                      const low = q < minq;

                      return (
                        <tr key={item.id} className={low ? "stocuri-row-low" : ""}>
                          <td className="stocuri-td-left">
                            <div className="stocuri-product">
                              <div className={cx("stocuri-dot", low && "stocuri-dot-low")} />
                              <div>
                                <div className="stocuri-product-name">{item.name}</div>
                                <div className="stocuri-product-meta">
                                  {low ? "Sub minim" : "OK"}
                                  {item.last_update ? ` • Last update: ${formatDateTime(item.last_update)}` : ""}
                                  {item.last_update_by ? ` • ${item.last_update_by}` : ""}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{categoryLabel(item.category)}</td>
                          <td className="stocuri-mono">{item.code}</td>
                          <td className="stocuri-qty">{q}</td>
                          <td className="stocuri-qty">{minq}</td>
                          <td>{role === 1 ? `${item.price} RON` : "—"}</td>
                          <td>
                            <div className="stocuri-row-actions">
                              {role === 1 && (
                                <>
                                  <button
                                    className="stocuri-btn stocuri-btn-success"
                                    onClick={() => adjustQuantity(item.id, "in")}
                                    title="Adaugă (adjust)"
                                  >
                                    ➕
                                  </button>
                                  <button
                                    className="stocuri-btn stocuri-btn-warning"
                                    onClick={() => adjustQuantity(item.id, "out")}
                                    title="Scade (adjust)"
                                  >
                                    ➖
                                  </button>
                                  <button
                                    className="stocuri-btn stocuri-btn-secondary"
                                    onClick={() => openEditProduct(item)}
                                    title="Editează produs"
                                  >
                                    ✏️
                                  </button>
                                </>
                              )}

                              <button
                                className={cx(
                                  "stocuri-btn",
                                  activeLogsStockId === item.id ? "stocuri-btn-info" : "stocuri-btn-ghost"
                                )}
                                onClick={() => fetchLogs(item.id)}
                                title="Istoric"
                              >
                                📜
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {visibleStock.length === 0 && (
                      <tr>
                        <td colSpan={7} className="stocuri-empty">
                          Niciun rezultat pentru filtrele curente.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {logs.length > 0 && (
                <div className="stocuri-logs">
                  <div className="stocuri-logs-header">
                    <div className="stocuri-logs-title">📜 Istoric modificări</div>
                    <button
                      className="stocuri-btn stocuri-btn-ghost"
                      onClick={() => {
                        setActiveLogsStockId(null);
                        setLogs([]);
                      }}
                    >
                      Închide
                    </button>
                  </div>

                  <div className="stocuri-logs-list">
                    {logs.map((log) => (
                      <div key={log.id} className="stocuri-log-row">
                        <div className="stocuri-log-left">
                          <span className="stocuri-log-icon">
                            {log.change_type === "in" ? "➕" : log.change_type === "out" ? "➖" : "↔️"}
                          </span>
                          <span className="stocuri-log-text">
                            {log.event_type ? `${log.event_type} • ` : ""}
                            {log.quantity ?? 0} buc. • {log.user_name || "Utilizator"}
                            {log.note ? ` • ${log.note}` : ""}
                          </span>
                        </div>
                        <div className="stocuri-log-date">{formatDateTime(log.created_at)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ORDERS TAB */}
      {activeTab === "orders" && (
        <>
          {ordersLoading && <div className="stocuri-state">Se încarcă comenzile…</div>}
          {ordersError && <div className="stocuri-state stocuri-state-error">{ordersError}</div>}

          {!ordersLoading && (
            <div className="stocuri-card">
              <div className="stocuri-orders">
                <div className="stocuri-orders-left">
                  <div className="stocuri-orders-header">
                    <div className="stocuri-orders-title">🧾 Lista comenzi</div>
                    <div className="stocuri-orders-meta">
                      {canSeeAllOrders ? "Toate comenzile" : "Doar comenzile mele"} • {filteredOrders.length} rezultate
                    </div>
                  </div>

                  <div className="stocuri-orders-list">
                    {filteredOrders.map((o) => (
                      <button
                        key={o.id}
                        className={cx("stocuri-order-card", selectedOrderId === o.id && "stocuri-order-card-active")}
                        onClick={() => fetchOrderDetails(o.id)}
                      >
                        <div className="stocuri-order-top">
                          <div className="stocuri-order-title">{o.title || `Comanda #${o.id}`}</div>
                          <div className="stocuri-order-status">{orderStatusLabel(o.status)}</div>
                        </div>
                        <div className="stocuri-order-sub">
                          <span>Delegat: {o.delegate_name || "—"}</span>
                          <span>Creat: {formatDateTime(o.created_at)}</span>
                        </div>
                      </button>
                    ))}

                    {filteredOrders.length === 0 && (
                      <div className="stocuri-empty stocuri-empty-orders">Nicio comandă.</div>
                    )}
                  </div>
                </div>

                <div className="stocuri-orders-right">
                  <div className="stocuri-orders-header">
                    <div className="stocuri-orders-title">📄 Detalii</div>
                    <div className="stocuri-orders-meta">
                      {selectedOrderId ? `#${selectedOrderId}` : "Selectează o comandă"}
                    </div>
                  </div>

                  {orderDetailsLoading && <div className="stocuri-state">Se încarcă detaliile…</div>}
                  {orderDetailsError && <div className="stocuri-state stocuri-state-error">{orderDetailsError}</div>}

                  {!orderDetailsLoading && !orderDetails && (
                    <div className="stocuri-empty stocuri-empty-orders">Alege o comandă din stânga.</div>
                  )}

                  {!orderDetailsLoading && orderDetails && (
                    <div className="stocuri-order-details">
                      <div className="stocuri-order-details-top">
                        <div>
                          <div className="stocuri-order-details-title">{orderDetails.order?.title}</div>
                          <div className="stocuri-order-details-sub">
                            {orderStatusLabel(orderDetails.order?.status)}{" "}
                            <span className="stocuri-muted">•</span> Delegat: {orderDetails.order?.delegate_name || "—"}{" "}
                            <span className="stocuri-muted">•</span> Creat: {formatDateTime(orderDetails.order?.created_at)}
                          </div>
                          <div className="stocuri-order-details-sub">
                            <span className="stocuri-muted">Asignați:</span>{" "}
                            {(orderDetails.assignees || []).length
                              ? (orderDetails.assignees || []).map((a) => a.name).join(", ")
                              : "—"}
                          </div>
                        </div>

                        <div className="stocuri-order-details-actions">
                          <button className="stocuri-btn stocuri-btn-ghost" onClick={() => fetchOrderDetails(selectedOrderId)}>
                            ⟲
                          </button>

                          <button
                            className="stocuri-btn stocuri-btn-secondary"
                            onClick={() => setShowNoteModal(true)}
                            title="Adaugă notă"
                          >
                            📝 Notă
                          </button>

                          {(role === 1 || role === 2 || role === 3) && (
                            <button className="stocuri-btn stocuri-btn-warning" onClick={mountItems} title="Custodie → Montat">
                              ✅ Montat
                            </button>
                          )}

                          {role === 1 && (
                            <button
                              className="stocuri-btn stocuri-btn-secondary"
                              onClick={issueToCustodie}
                              title="Depozit → Custodie"
                            >
                              🏢 Trimite cantitățile (Custodie)
                            </button>
                          )}

                          {(role === 1 || role === 2 || role === 3) && (
                            <button className="stocuri-btn stocuri-btn-success" onClick={closeOrder} title="Închide comanda">
                              🔒 Închide
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="stocuri-split">
                        <div className="stocuri-split-left">
                          <div className="stocuri-section-title">Produse</div>

                          <div className="stocuri-table-responsive">
                            <table className="stocuri-table stocuri-table-compact">
                              <thead>
                                <tr>
                                  <th style={{ textAlign: "left" }}>Produs</th>
                                  <th>Cerut</th>
                                  <th>În custodie</th>
                                  <th>Montat</th>
                                  <th style={{ width: 220 }}>Acțiuni</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(orderDetails.items || []).map((it) => {
                                  const requested = Number(it.requested_qty) || 0;
                                  const issued = Number(it.issued_qty) || 0;
                                  const mounted = Number(it.mounted_qty) || 0;

                                  return (
                                    <tr key={it.id}>
                                      <td className="stocuri-td-left">
                                        <div className="stocuri-product">
                                          <div className="stocuri-dot" />
                                          <div>
                                            <div className="stocuri-product-name">{it.name}</div>
                                            <div className="stocuri-product-meta">{categoryLabel(it.category)}</div>
                                          </div>
                                        </div>
                                      </td>

                                      <td className="stocuri-qty">{requested}</td>
                                      <td className="stocuri-qty">{issued}</td>
                                      <td className="stocuri-qty">{mounted}</td>

                                      <td>
                                        <div className="stocuri-inline-actions">
                                          {role === 1 && (
                                            <div className="stocuri-inline-block">
                                              <div className="stocuri-inline-label">Custodie</div>
                                              <div className="stocuri-inline-row">
                                                <input
                                                  className="stocuri-input stocuri-input-xs"
                                                  type="number"
                                                  min={0}
                                                  placeholder="0"
                                                  value={issueDraft[it.id] ?? ""}
                                                  onChange={(e) =>
                                                    setIssueDraft((p) => ({ ...p, [it.id]: e.target.value }))
                                                  }
                                                />
                                                <button
                                                  className="stocuri-btn stocuri-btn-xs stocuri-btn-secondary"
                                                  onClick={() => issueSingleToCustodie(it.id)}
                                                  title="Depozit → Custodie"
                                                >
                                                  🏢
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          {(role === 1 || role === 2 || role === 3) && (
                                            <div className="stocuri-inline-block">
                                              <div className="stocuri-inline-label">Montat</div>
                                              <div className="stocuri-inline-row">
                                                <input
                                                  className="stocuri-input stocuri-input-xs"
                                                  type="number"
                                                  min={0}
                                                  placeholder="0"
                                                  value={mountDraft[it.id] ?? ""}
                                                  onChange={(e) =>
                                                    setMountDraft((p) => ({ ...p, [it.id]: e.target.value }))
                                                  }
                                                />
                                                <button
                                                  className="stocuri-btn stocuri-btn-xs stocuri-btn-warning"
                                                  onClick={() => mountSingleItem(it.id)}
                                                  title="Custodie → Montat"
                                                >
                                                  ✅
                                                </button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}

                                {(orderDetails.items || []).length === 0 && (
                                  <tr>
                                    <td colSpan={5} className="stocuri-empty">
                                      Nu există produse în această comandă.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="stocuri-hint">
                            💡 Completează cantitățile pe rânduri și folosește butoanele 🏢 (custodie) / ✅ (montat). Sau
                            folosește butoanele mari din dreapta sus.
                          </div>
                        </div>

                        <div className="stocuri-split-right">
                          <div className="stocuri-section-title">Note</div>

                          <div className="stocuri-notes">
                            {(orderDetails.notes || []).map((n) => (
                              <div key={n.id} className="stocuri-note">
                                <div className="stocuri-note-top">
                                  <div className="stocuri-note-user">{n.user_name || "Utilizator"}</div>
                                  <div className="stocuri-note-date">{formatDateTime(n.created_at)}</div>
                                </div>
                                <div className="stocuri-note-text">{n.note}</div>
                              </div>
                            ))}

                            {(orderDetails.notes || []).length === 0 && (
                              <div className="stocuri-empty stocuri-empty-orders">Nu există note.</div>
                            )}
                          </div>

                          <div className="stocuri-section-title" style={{ marginTop: 14 }}>
                            Istoric comandă
                          </div>

                          <div className="stocuri-events">
                            {(orderDetails.events || []).map((ev) => (
                              <div key={ev.id} className="stocuri-event-row">
                                <div className="stocuri-event-left">
                                  <span className="stocuri-event-icon">🕘</span>
                                  <span className="stocuri-event-text">
                                    <b>{ev.event_type}</b> • {ev.user_name || "Utilizator"}
                                    {ev.note ? ` • ${ev.note}` : ""}
                                  </span>
                                </div>
                                <div className="stocuri-event-date">{formatDateTime(ev.created_at)}</div>
                              </div>
                            ))}

                            {(orderDetails.events || []).length === 0 && (
                              <div className="stocuri-empty stocuri-empty-orders">Nu există istoric.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========================= */}
      {/* MODALS */}
      {/* ========================= */}


      {/* NOTE MODAL (role 1/2/3) */}
      <Modal
        open={showNoteModal}
        title="📝 Adaugă notă"
        onClose={() => setShowNoteModal(false)}
        footer={
          <div className="stocuri-modal-actions">
            <button className="stocuri-btn stocuri-btn-ghost" onClick={() => setShowNoteModal(false)}>
              Renunță
            </button>
            <button
              className="stocuri-btn stocuri-btn-secondary"
              onClick={() => {
                addOrderNote();
                setShowNoteModal(false);
              }}
            >
              Salvează
            </button>
          </div>
        }
        width="min(720px, 100%)"
      >
        <div className="stocuri-form">
          <label>Notă</label>
          <textarea
            className="stocuri-textarea"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="ex: produse folosite la lucrarea X…"
            rows={6}
          />
        </div>
      </Modal>

      {/* ADD STOCK (role 1) */}
      <Modal
        open={showAddModal}
        title="➕ Adaugă produs în stoc"
        onClose={() => setShowAddModal(false)}
        footer={
          <div className="stocuri-modal-actions">
            <button className="stocuri-btn stocuri-btn-ghost" onClick={() => setShowAddModal(false)}>
              Renunță
            </button>
            <button className="stocuri-btn stocuri-btn-primary" onClick={saveAddStock}>
              Salvează
            </button>
          </div>
        }
      >
        <div className="stocuri-form">
          <div className="stocuri-form-grid">
            <div>
              <label>Nume produs</label>
              <input className="stocuri-input" name="name" value={addForm.name} onChange={onAddInputChange} />
            </div>

            <div>
              <label>Categorie</label>
              <select className="stocuri-select" name="category" value={addForm.category} onChange={onAddInputChange}>
                <option value="panou">Panou</option>
                <option value="acumulator">Acumulator</option>
                <option value="invertor">Invertor</option>
                <option value="smart_meter">Smart Meter</option>
                <option value="altele">Altele</option>
              </select>
            </div>

            <div>
              <label>Cantitate</label>
              <input
                className="stocuri-input"
                name="quantity"
                type="number"
                min={0}
                value={addForm.quantity}
                onChange={onAddInputChange}
              />
            </div>

            <div>
              <label>Minim</label>
              <input
                className="stocuri-input"
                name="min_quantity"
                type="number"
                min={0}
                value={addForm.min_quantity}
                onChange={onAddInputChange}
              />
            </div>

            <div>
              <label>Preț (RON)</label>
              <input
                className="stocuri-input"
                name="price"
                type="number"
                min={0}
                value={addForm.price}
                onChange={onAddInputChange}
              />
            </div>
          </div>

          <div className="stocuri-divider" />

          <div className="stocuri-codes">
            <div className="stocuri-section-title">Coduri produse</div>
            <div className="stocuri-codes-grid">
              {addCodes.map((code, idx) => (
                <input
                  key={idx}
                  className="stocuri-input stocuri-input-code"
                  placeholder={`Cod #${idx + 1}`}
                  value={code}
                  onChange={(e) => onAddCodeChange(idx, e.target.value)}
                />
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* EDIT PRODUCT (role 1) */}
      <Modal
        open={showEditModal}
        title="✏️ Editare produs"
        onClose={() => setShowEditModal(false)}
        footer={
          <div className="stocuri-modal-actions">
            <button className="stocuri-btn stocuri-btn-ghost" onClick={() => setShowEditModal(false)}>
              Renunță
            </button>
            <button className="stocuri-btn stocuri-btn-primary" onClick={saveEditProduct}>
              Salvează
            </button>
          </div>
        }
      >
        <div className="stocuri-form">
          <div className="stocuri-form-grid">
            <div>
              <label>Nume</label>
              <input
                className="stocuri-input"
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>

            <div>
              <label>Minim</label>
              <input
                className="stocuri-input"
                type="number"
                min={0}
                value={editForm.min_quantity}
                onChange={(e) => setEditForm((p) => ({ ...p, min_quantity: e.target.value }))}
              />
            </div>

            <div>
              <label>Preț (RON)</label>
              <input
                className="stocuri-input"
                type="number"
                min={0}
                value={editForm.price}
                onChange={(e) => setEditForm((p) => ({ ...p, price: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* QUICK MOVE TO CUSTODIE (role 1) */}
      <Modal
        open={showCustodieModal}
        title="🏢 Scoate rapid în Custodie"
        onClose={() => setShowCustodieModal(false)}
        footer={
          <div className="stocuri-modal-actions">
            <button className="stocuri-btn stocuri-btn-ghost" onClick={() => setShowCustodieModal(false)}>
              Renunță
            </button>
            <button className="stocuri-btn stocuri-btn-secondary" onClick={() => moveToCustodieQuick(selectedCodes)}>
              Confirmă mutarea
            </button>
          </div>
        }
      >
        <div className="stocuri-form">
          <div className="stocuri-form-grid">
            <div>
              <label>Categorie</label>
              <select
                className="stocuri-select"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedCodes([]);
                  fetchProductsByCategory(e.target.value);
                }}
              >
                <option value="">Selectează…</option>
                <option value="panou">Panou</option>
                <option value="acumulator">Acumulator</option>
                <option value="invertor">Invertor</option>
                <option value="smart_meter">Smart Meter</option>
                <option value="altele">Altele</option>
              </select>
            </div>

            <div>
              <label>Caută cod</label>
              <input
                className="stocuri-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="ex: ABC123"
              />
            </div>
          </div>

          <div className="stocuri-divider" />

          <div className="stocuri-pick-list">
            {(filteredProducts || [])
              .filter((p) => {
                const s = (searchTerm || "").trim().toLowerCase();
                if (!s) return true;
                return String(p.code || "").toLowerCase().includes(s) || String(p.name || "").toLowerCase().includes(s);
              })
              .slice(0, 220)
              .map((p) => (
                <button
                  key={p.id}
                  className={cx("stocuri-pick-row", selectedCodes.includes(p.code) && "stocuri-pick-row-active")}
                  onClick={() => toggleCodeSelected(p.code)}
                >
                  <div className="stocuri-pick-left">
                    <div className="stocuri-pick-title">{p.name}</div>
                    <div className="stocuri-pick-sub">
                      {categoryLabel(p.category)} • <span className="stocuri-mono">{p.code}</span>
                    </div>
                  </div>
                  <div className="stocuri-pick-right">{selectedCodes.includes(p.code) ? "✓" : "+"}</div>
                </button>
              ))}
          </div>
        </div>
      </Modal>

      {/* MOVE BACK TO DEPOZIT (role 1) */}
      <Modal
        open={showMoveBackModal}
        title="↩️ Mută înapoi în Depozit"
        onClose={() => setShowMoveBackModal(false)}
        footer={
          <div className="stocuri-modal-actions">
            <button className="stocuri-btn stocuri-btn-ghost" onClick={() => setShowMoveBackModal(false)}>
              Renunță
            </button>
            <button className="stocuri-btn stocuri-btn-secondary" onClick={moveToDepozit}>
              Mută în depozit
            </button>
          </div>
        }
      >
        <div className="stocuri-form">
          <div className="stocuri-form-grid">
            <div>
              <label>Categorie</label>
              <select
                className="stocuri-select"
                value={moveBackCategory}
                onChange={(e) => {
                  const cat = e.target.value;
                  setMoveBackCategory(cat);
                  setMoveBackSelectedCodes([]);
                  fetchCustodieProductsByCategory(cat);
                }}
              >
                <option value="">Selectează…</option>
                <option value="panou">Panou</option>
                <option value="acumulator">Acumulator</option>
                <option value="invertor">Invertor</option>
                <option value="smart_meter">Smart Meter</option>
                <option value="altele">Altele</option>
              </select>
            </div>

            <div>
              <label>Caută</label>
              <input
                className="stocuri-input"
                value={moveBackSearch}
                onChange={(e) => setMoveBackSearch(e.target.value)}
                placeholder="nume / cod…"
              />
            </div>
          </div>

          <div className="stocuri-divider" />

          <div className="stocuri-pick-list">
            {(moveBackProducts || [])
              .filter((p) => {
                const s = (moveBackSearch || "").trim().toLowerCase();
                if (!s) return true;
                return (
                  String(p.code || "").toLowerCase().includes(s) ||
                  String(p.name || "").toLowerCase().includes(s) ||
                  String(p.category || "").toLowerCase().includes(s)
                );
              })
              .slice(0, 260)
              .map((p) => (
                <button
                  key={p.id}
                  className={cx("stocuri-pick-row", moveBackSelectedCodes.includes(p.code) && "stocuri-pick-row-active")}
                  onClick={() => toggleMoveBackCode(p.code)}
                >
                  <div className="stocuri-pick-left">
                    <div className="stocuri-pick-title">{p.name}</div>
                    <div className="stocuri-pick-sub">
                      {categoryLabel(p.category)} • <span className="stocuri-mono">{p.code}</span>
                    </div>
                  </div>
                  <div className="stocuri-pick-right">{moveBackSelectedCodes.includes(p.code) ? "✓" : "+"}</div>
                </button>
              ))}
          </div>

          <div className="stocuri-hint">
            Selectate: <b>{moveBackSelectedCodes.length}</b>
          </div>
        </div>
      </Modal>

      {/* CREATE ORDER (role 1) */}
      <Modal
        open={showCreateOrder}
        title="➕ Comandă nouă"
        onClose={() => setShowCreateOrder(false)}
        footer={
          <div className="stocuri-modal-actions">
            <button className="stocuri-btn stocuri-btn-ghost" onClick={() => setShowCreateOrder(false)}>
              Renunță
            </button>
            <button className="stocuri-btn stocuri-btn-primary" onClick={createOrder}>
              Creează
            </button>
          </div>
        }
        width="min(980px, 100%)"
      >
        <div className="stocuri-form">
          <div className="stocuri-form-grid">
            <div>
              <label>Titlu comandă</label>
              <input
                className="stocuri-input"
                value={createOrderForm.title}
                onChange={(e) => setCreateOrderForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="ex: Comandă echipa 2 • 19/01"
              />
            </div>

            <div>
              <label>Delegat</label>
              <input
                className="stocuri-input"
                value={createOrderForm.delegate_name}
                onChange={(e) => setCreateOrderForm((p) => ({ ...p, delegate_name: e.target.value }))}
                placeholder="Nume delegat"
              />
            </div>
          </div>

          <div className="stocuri-divider" />

          <div className="stocuri-section-title">Asignați utilizatori</div>

          {usersLoading ? (
            <div className="stocuri-state">Se încarcă utilizatorii…</div>
          ) : usersList.length === 0 ? (
            <div className="stocuri-hint">
              ⚠️ Dacă nu ai endpoint `get_users`, poți lăsa lista goală.
            </div>
          ) : (
            <div className="stocuri-assignees">
              {usersList.map((u) => (
                <label key={u.id} className="stocuri-check stocuri-check-pill">
                  <input
                    type="checkbox"
                    checked={createOrderForm.assignee_ids.includes(Number(u.id))}
                    onChange={() => toggleAssignee(u.id)}
                  />
                  <span>{u.name}</span>
                </label>
              ))}
            </div>
          )}

          <div className="stocuri-divider" />

          <div className="stocuri-section-title">Produse</div>

          <div className="stocuri-inline-row" style={{ gap: 10, marginBottom: 10 }}>
            <button
              className="stocuri-btn stocuri-btn-secondary"
              onClick={() => {
                setProductPickerOpen(true);
                fetchProductsForPicker();
              }}
            >
              ➕ Adaugă produs
            </button>

            <div className="stocuri-hint" style={{ margin: 0 }}>
              Adaugi produsele și cantitățile cerute.
            </div>
          </div>

          {productPickerOpen && (
            <div className="stocuri-picker">
              <div className="stocuri-picker-top">
                <div className="stocuri-search">
                  <span className="stocuri-search-icon">⌕</span>
                  <input
                    value={productPickerSearch}
                    onChange={(e) => setProductPickerSearch(e.target.value)}
                    placeholder="Caută produs…"
                  />
                </div>

                <button className="stocuri-btn stocuri-btn-ghost" onClick={() => setProductPickerOpen(false)}>
                  Închide
                </button>
              </div>

              {productsPickerLoading && <div className="stocuri-state">Se încarcă produsele…</div>}
              {productsPickerError && <div className="stocuri-state stocuri-state-error">{productsPickerError}</div>}

              {!productsPickerLoading && (
                <div className="stocuri-picker-list">
                  {(productsForPicker || [])
                    .filter((p) => {
                      const s = (productPickerSearch || "").trim().toLowerCase();
                      if (!s) return true;
                      const hay = `${p.name || ""} ${p.category || ""}`.toLowerCase();
                      return hay.includes(s);
                    })
                    .slice(0, 180)
                    .map((p) => (
                      <button
                        key={p.product_id ?? p.id ?? `${p.name}-${p.category}`}
                        className="stocuri-pick-row"
                        onClick={() => addItemToOrder(p)}
                      >
                        <div className="stocuri-pick-left">
                          <div className="stocuri-pick-title">{p.name}</div>
                          <div className="stocuri-pick-sub">{categoryLabel(p.category)}</div>
                        </div>
                        <div className="stocuri-pick-right">+</div>
                      </button>
                    ))}

                  {(productsForPicker || []).length === 0 && (
                    <div className="stocuri-empty stocuri-empty-orders">Nu există produse disponibile.</div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="stocuri-table-responsive" style={{ marginTop: 10 }}>
            <table className="stocuri-table stocuri-table-compact">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Produs</th>
                  <th>Cantitate cerută</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {(createOrderForm.items || []).map((it) => (
                  <tr key={it.product_id}>
                    <td className="stocuri-td-left">
                      <div className="stocuri-product">
                        <div className="stocuri-dot" />
                        <div>
                          <div className="stocuri-product-name">{it.name}</div>
                          <div className="stocuri-product-meta">{categoryLabel(it.category)}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <input
                        className="stocuri-input stocuri-input-xs"
                        type="number"
                        min={0}
                        value={it.requested_qty}
                        onChange={(e) => setOrderItemQty(it.product_id, e.target.value)}
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="stocuri-btn stocuri-btn-ghost"
                        onClick={() => removeOrderItem(it.product_id)}
                        title="Șterge"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}

                {(createOrderForm.items || []).length === 0 && (
                  <tr>
                    <td colSpan={3} className="stocuri-empty">
                      Nu ai adăugat produse.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Stock;
